import React, { useState } from 'react'
import MuiAppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { Link as RouterLink } from 'react-router-dom'
import { CoreAppToolbar } from './CoreAppToolbar'
import { OnlineUsersDisplay } from 'components/OnlineUsersDisplay/OnlineUsersDisplay'
import { useAuth } from 'hooks/auth/useAuth'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { APP_VERSION } from '@/constants/appVersion'
import { getDocsChangelogUrl, getDocsHomeUrl, getGitHubBoardUrl } from '@/constants/docsUrl'
import { APPBAR_HEIGHT, APPBAR_HEIGHT_DESKTOP, CHROME_GAP, CHROME_RADIUS, SAFE_AREA_TOP } from 'core/theme.tokens'

interface CoreAppBarProps {
  expanded: boolean
  onMenuClick: () => void
}

const LOGO_SRC =
  'https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FIcon-OpticTruckWorks-03.png?alt=media&token=ca5d0d5d-088e-46b5-957d-0ed5b93c3acc'

/** Portal staff only. Never clients, bank agents, transporters, or shop roles. */
const canSeeAdminDocsLinks = (role: string | undefined): boolean => role === 'admin' || role === 'babyAdmin'

const homePathForRole = (role: string | undefined): string => {
  if (role === 'client') return '/client'
  if (role === 'bankAgent') return '/bank'
  return '/admin'
}

export const CoreAppBar: React.FC<CoreAppBarProps> = ({ expanded, onMenuClick }) => {
  const { userInfo } = useAuth()
  const isMobile = useIsMobile()
  const canSeeDocs = canSeeAdminDocsLinks(userInfo?.role)
  const homePath = homePathForRole(userInfo?.role)
  const [versionMenuEl, setVersionMenuEl] = useState<null | HTMLElement>(null)
  const versionMenuOpen = Boolean(versionMenuEl)

  const closeVersionMenu = () => setVersionMenuEl(null)

  const logo = (
    <Box
      component='img'
      src={LOGO_SRC}
      alt='Optic Truck Works'
      sx={{
        maxHeight: { xs: 32, sm: 28 },
        width: 'auto',
        objectFit: 'contain',
        borderRadius: '8px',
        display: 'block',
      }}
    />
  )

  return (
    <MuiAppBar
      position='fixed'
      elevation={0}
      sx={{
        top: isMobile ? 0 : `calc(${CHROME_GAP}px + ${SAFE_AREA_TOP})`,
        left: isMobile ? 0 : CHROME_GAP,
        right: isMobile ? 0 : CHROME_GAP,
        width: 'auto',
        height: isMobile ? `calc(${APPBAR_HEIGHT}px + ${SAFE_AREA_TOP})` : APPBAR_HEIGHT_DESKTOP,
        pt: isMobile ? SAFE_AREA_TOP : 0,
        borderRadius: isMobile ? 0 : `${CHROME_RADIUS}px`,
        overflow: 'visible',
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'auto minmax(0, 1fr) auto',
            md: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          },
          alignItems: 'center',
          columnGap: { xs: 1, sm: 1.5 },
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          boxSizing: 'border-box',
          minHeight: {
            xs: `${APPBAR_HEIGHT}px`,
            sm: `${APPBAR_HEIGHT_DESKTOP}px`,
          },
          px: { xs: 1, sm: 1.5 },
          '& .MuiIconButton-root': {
            minWidth: { xs: 44, sm: 36 },
            minHeight: { xs: 44, sm: 36 },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {isMobile ? (
            <IconButton
              color='inherit'
              aria-label={expanded ? 'close drawer' : 'open drawer'}
              onClick={onMenuClick}
              edge='start'
            >
              {expanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              component={RouterLink}
              to={homePath}
              aria-label='Go to dashboard'
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              {logo}
            </Box>
            {canSeeDocs ? (
              <>
                <Button
                  color='inherit'
                  size='small'
                  aria-haspopup='menu'
                  aria-expanded={versionMenuOpen}
                  aria-controls={versionMenuOpen ? 'admin-version-menu' : undefined}
                  aria-label={`Version ${APP_VERSION} links`}
                  onClick={event => setVersionMenuEl(event.currentTarget)}
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    py: 0.25,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    lineHeight: 1.2,
                    textTransform: 'none',
                    opacity: 0.85,
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', md: 'inline-flex' },
                    borderRadius: 1.5,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  v{APP_VERSION}
                </Button>
                <Menu
                  id='admin-version-menu'
                  anchorEl={versionMenuEl}
                  open={versionMenuOpen}
                  onClose={closeVersionMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem
                    component='a'
                    href={getDocsChangelogUrl()}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={closeVersionMenu}
                  >
                    What’s new
                  </MenuItem>
                  <MenuItem
                    component='a'
                    href={getDocsHomeUrl()}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={closeVersionMenu}
                  >
                    Portal docs
                  </MenuItem>
                  <MenuItem
                    component='a'
                    href={getGitHubBoardUrl()}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={closeVersionMenu}
                  >
                    GitHub board
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            {!isMobile ? <OnlineUsersDisplay compact /> : null}
          </Box>
        </Box>
        <CoreAppToolbar />
      </Box>
    </MuiAppBar>
  )
}
